import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Table, Modal, Button } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQueuedFieldSave } from 'hooks/useQueuedFieldSave';
import { isValidHostname, isPortValid } from 'utils/utils';

const EMPTY_CONFIG = { configs: [] };
const BLANK_ROW = { host: '', port: '', protocol: 'udp' };
const MAX_COLLECTORS = 3;

/**
 * An editable list of collector destinations (host, port, protocol) for one user, edited in a modal.
 *
 * @param {object} props
 * @param {string|number} props.userId the selected user
 * @param {boolean} props.editable whether the current admin may change it
 * @param {{collectors: Array}} props.serverConfig the stored config of the user
 * @param {Function} props.updateConfig (userId, params) => Promise, the request that saves it
 * @param {string} props.listKey request key of the collector list
 * @param {string} props.errorPrefix prefix of the backend error codes, for the field messages
 * @param {{id: string, defaultMessage: string}} props.label label of the section
 * @param {{id: string, defaultMessage: string}} props.hint what is sent where, shown under the label
 * @param {{id: string, defaultMessage: string}} props.titleLabel title of the modal
 * @param {Function} [props.refetchNetwork] called after a successful save
 */
const UserCollectorConfig = ({
  userId,
  editable,
  serverConfig,
  updateConfig,
  listKey,
  errorPrefix,
  label,
  hint,
  titleLabel,
  refetchNetwork,
}) => {
  const intl = useIntl();
  const { value: config, save: saveField, reseed } = useQueuedFieldSave(EMPTY_CONFIG);
  const [modal, setModal] = useState(null);
  const [modalErrors, setModalErrors] = useState({});

  const serverCollectorsKey = JSON.stringify(serverConfig?.collectors || []);

  useEffect(() => {
    reseed(
      {
        configs: JSON.parse(serverCollectorsKey).map((row) => ({ host: row.host || '', port: row.port || '', protocol: row.protocol || 'udp' })),
      },
      userId
    );
  }, [userId, serverCollectorsKey, reseed]);

  const validateRow = ({ host, port }) => {
    const errors = {};
    const trimmedHost = host.trim();
    if (!trimmedHost) errors.host = intl.formatMessage({ id: 'GENERAL.REQUIRED_FIELD' });
    else if (!isValidHostname(trimmedHost)) errors.host = intl.formatMessage({ id: `${errorPrefix}_WRONG_HOST` });
    if (`${port}`.trim() === '') errors.port = intl.formatMessage({ id: 'GENERAL.REQUIRED_FIELD' });
    else if (!/^\d+$/.test(`${port}`) || !isPortValid(port)) errors.port = intl.formatMessage({ id: `${errorPrefix}_WRONG_PORT` });
    return errors;
  };

  const saveConfig = (next) =>
    saveField((value) => updateConfig(userId, { [listKey]: value.configs }), next, { userId, refetchNetwork });

  const openModal = () => {
    setModalErrors({});
    const rows = config.configs.map((row) => ({ ...row }));
    setModal({ rows: rows.length > 0 ? rows : [{ ...BLANK_ROW }] });
  };

  const closeModal = () => {
    setModal(null);
    setModalErrors({});
  };

  const updateRow = (index, changes) => {
    setModal((prev) => ({ ...prev, rows: prev.rows.map((row, i) => (i === index ? { ...row, ...changes } : row)) }));
    setModalErrors((prev) => ({ ...prev, [index]: null }));
  };

  const addRow = () => setModal((prev) => (prev.rows.length >= MAX_COLLECTORS ? prev : { ...prev, rows: [...prev.rows, { ...BLANK_ROW }] }));

  const removeRow = (index) => {
    setModal((prev) => ({ ...prev, rows: prev.rows.filter((_, i) => i !== index) }));
    setModalErrors({});
  };

  const handleModalSave = () => {
    const errors = {};
    modal.rows.forEach((row, index) => {
      const rowErrors = validateRow(row);
      if (Object.keys(rowErrors).length > 0) errors[index] = rowErrors;
    });
    setModalErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const configs = modal.rows.map((row) => ({ host: row.host.trim(), port: Number(row.port), protocol: row.protocol }));
    saveConfig({ configs }).then((saved) => {
      if (saved) closeModal();
    });
  };

  const limitReached = config.configs.length >= MAX_COLLECTORS;

  return (
    <>
      <hr className="my-3" />
      <Form.Group as={Row} className="align-items-center mb-3">
        <Col md={6}>
          <Form.Label className="mb-0 font-weight-bold text-nowrap">
            <FormattedMessage id={label.id} defaultMessage={label.defaultMessage} />
          </Form.Label>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end">
          <Button
            className="py-1 px-2"
            aria-label={intl.formatMessage({ id: 'VPNCLIENTCONFIG.ADD_COLLECTOR', defaultMessage: 'Add collector' })}
            disabled={!editable}
            onClick={() => openModal()}
          >
            <AddIcon fontSize="medium" />
          </Button>
        </Col>
        <Col xs={12}>
          <Form.Text className="text-muted">
            <FormattedMessage id={hint.id} defaultMessage={hint.defaultMessage} />
          </Form.Text>
        </Col>
        {limitReached && (
          <Col xs={12}>
            <Form.Text className="text-muted">
              <FormattedMessage id="VPNCLIENTCONFIG.COLLECTOR_LIMIT_REACHED" defaultMessage="Maximum {max} collectors" values={{ max: MAX_COLLECTORS }} />
            </Form.Text>
          </Col>
        )}
      </Form.Group>
      {config.configs.length > 0 && (
        <Table size="sm" className="mt-2 mb-4">
          <thead>
            <tr>
              <th><FormattedMessage id="VPNCLIENTCONFIG.COLLECTOR_HOST" defaultMessage="Host" /></th>
              <th><FormattedMessage id="VPNCLIENTCONFIG.COLLECTOR_PROTOCOL" defaultMessage="Protocol" /></th>
              <th><FormattedMessage id="VPNCLIENTCONFIG.COLLECTOR_PORT" defaultMessage="Port" /></th>
            </tr>
          </thead>
          <tbody>
            {config.configs.map((row, index) => (
              <tr key={`${row.host}-${row.port}-${row.protocol}-${index}`}>
                <td>{row.host}</td>
                <td>{`${row.protocol}`.toUpperCase()}</td>
                <td>{row.port}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Modal show={modal !== null} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title><FormattedMessage id={titleLabel.id} defaultMessage={titleLabel.defaultMessage} /></Modal.Title>
        </Modal.Header>
        {modal && (
          <Modal.Body>
            {modal.rows.map((row, index) => (
              <Row key={index} className="mb-3 align-items-start">
                <Col md={5}>
                  {index === 0 && <Form.Label><FormattedMessage id="VPNCLIENTCONFIG.COLLECTOR_HOST" defaultMessage="Host" /></Form.Label>}
                  <Form.Control
                    type="text"
                    isInvalid={!!modalErrors[index]?.host}
                    value={row.host}
                    onChange={(e) => updateRow(index, { host: e.target.value })}
                  />
                  <Form.Control.Feedback type="invalid">{modalErrors[index]?.host}</Form.Control.Feedback>
                </Col>
                <Col md={3}>
                  {index === 0 && <Form.Label><FormattedMessage id="VPNCLIENTCONFIG.COLLECTOR_PROTOCOL" defaultMessage="Protocol" /></Form.Label>}
                  <Form.Control as="select" value={row.protocol} onChange={(e) => updateRow(index, { protocol: e.target.value })}>
                    <option value="udp">UDP</option>
                    <option value="tcp">TCP</option>
                  </Form.Control>
                </Col>
                <Col md={3}>
                  {index === 0 && <Form.Label><FormattedMessage id="VPNCLIENTCONFIG.COLLECTOR_PORT" defaultMessage="Port" /></Form.Label>}
                  <Form.Control
                    type="number"
                    min={1}
                    max={65535}
                    isInvalid={!!modalErrors[index]?.port}
                    value={row.port}
                    onChange={(e) => updateRow(index, { port: e.target.value })}
                  />
                  <Form.Control.Feedback type="invalid">{modalErrors[index]?.port}</Form.Control.Feedback>
                </Col>
                <Col md={1} className={index === 0 ? 'pt-4' : ''}>
                  <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removeRow(index)}>
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Col>
              </Row>
            ))}
            <Button variant="outline-primary" size="sm" disabled={modal.rows.length >= MAX_COLLECTORS} onClick={addRow}>
              <AddIcon fontSize="small" /> <FormattedMessage id="VPNCLIENTCONFIG.ADD_COLLECTOR" defaultMessage="Add collector" />
            </Button>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            <FormattedMessage id="GENERAL.CANCEL" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleModalSave}>
            <FormattedMessage id="GENERAL.SAVE" defaultMessage="Save" />
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserCollectorConfig;
